import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as authService from "../services/authService";

function LoginPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;

    setErrorMsg(null);
    setLoading(true);

    try {
      await authService.login({ email, password });
      navigate("/home");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al iniciar sesión";
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-background-light dark:bg-background-dark font-display text-white antialiased overflow-x-hidden min-h-screen flex flex-col">
      <div className="relative flex flex-grow w-full flex-col justify-center items-center">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 h-full w-full overflow-hidden">
          <img
            className="h-full w-full object-cover transition-transform duration-1000 hover:scale-105"
            data-alt="Elegant wedding table setting with white flowers and glassware"
            alt=""
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuC38FsklWVkpTSDc___5ejbOBAc31Ckaad0120k-UrGM-grR4GOjUmsAZWfkKE1j_c89t1rotP_M1ibmA61Z3Jvz2lLVFU8KMJGMwawhUGD35mhecZYVczqruWQthGXELMSX1BTymzyNGIE2ZB3ej9mwsZMJZhC806hgl0ySA6GUC6vablKrZCXvJCfuudDfz7k5CX_VoHOl-xjA2ESyflRCSWDuVBgW5-jntezEP3qiXLWd4lJT7UqLHFtGGMnupdnDLG6AqamoSNL"
          />
          <div className="absolute inset-0 bg-[#141118]/80 backdrop-blur-[2px]"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#141118] via-transparent to-[#141118]/40"></div>
        </div>

        {/* Main Layout Container */}
        <div className="relative z-10 w-full px-4 py-8 flex justify-center">
          {/* Login Card */}
          <div className="glass-panel w-full max-w-[480px] rounded-2xl border border-border-dark/50 shadow-2xl p-6 sm:p-10 md:p-12 animate-fade-in-up">
            {/* Logo & Header */}
            <div className="flex flex-col items-center text-center mb-8">
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                <span className="material-symbols-outlined text-3xl text-primary">
                  diamond
                </span>
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-white mb-2">
                Wedding Planner App
              </h2>
              <p className="text-text-muted text-sm font-normal">
                Bienvenido nuevamente! Ingresa tus credenciales.
              </p>
            </div>

            {/* Form */}
            <form
              className="flex flex-col gap-5"
              method="POST"
              onSubmit={handleSubmit}
            >
              {/* Email Input */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-white" htmlFor="email">
                  Correo
                </label>
                <div className="relative flex items-center">
                  <input
                    className="w-full rounded-lg border border-border-dark bg-surface-dark text-white placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                    id="email"
                    name="email"
                    placeholder="planner@example.com"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="flex flex-col gap-2">
                <label
                  className="text-sm font-medium text-white"
                  htmlFor="password"
                >
                  Contraseña
                </label>
                <div className="relative flex items-center">
                  <input
                    className="w-full rounded-lg border border-border-dark bg-surface-dark text-white placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary h-12 pl-4 pr-12 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                    id="password"
                    name="password"
                    placeholder="••••••••"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={loading}
                    required
                  />
                  <button
                    className="absolute right-0 top-0 bottom-0 px-3 flex items-center justify-center text-text-muted hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={loading}
                    aria-label={
                      showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                    }
                  >
                    <span className="material-symbols-outlined text-xl">
                      {showPassword ? "visibility" : "visibility_off"}
                    </span>
                  </button>
                </div>
              </div>

              {errorMsg && <p className="text-sm text-red-300 -mt-2">{errorMsg}</p>}

              {/* Login Button */}
              <button
                className="mt-2 w-full rounded-lg bg-primary h-12 px-4 text-base font-semibold text-white shadow-lg shadow-primary/25 hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-[#191121] transition-all duration-200 transform active:scale-[0.98] disabled:opacity-80 disabled:cursor-wait disabled:hover:bg-primary disabled:active:scale-100"
                type="submit"
                disabled={loading}
                aria-busy={loading}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {loading ? (
                    <span className="material-symbols-outlined text-[20px] animate-spin">progress_activity</span>
                  ) : (
                    <span className="material-symbols-outlined text-[20px]">login</span>
                  )}
                  <span>{loading ? "Validando acceso..." : "Ingresar"}</span>
                </span>
              </button>
            </form>

            {/* Footer */}
            <p className="mt-8 text-center text-sm text-text-muted">
              No tienes una cuenta?{" "}
              <button
                className="font-medium text-primary hover:text-primary/80 transition-colors"
                type="button"
              >
                Contacta con nosotros
              </button>
            </p>
          </div>
        </div>

        {/* Decorative Bottom Elements */}
        <div className="relative z-10 mt-auto w-full pb-6 text-center">
          <p className="text-xs text-text-muted/50">
            © 2026 Wedding Planner App. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
